import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { ethers } from "ethers";

import { z } from "zod";

// Schema for minting parameters
export const MintPrivateERC20Schema = z.object({
    tokenAddress: z.string().describe("ERC20 token contract address"),
    toAddress: z.string().describe("Address to receive the minted tokens"),
    amount: z.string().describe("Amount of tokens to mint"),
    gasLimit: z.number().optional().describe("Optional gas limit"),
});

export type MintPrivateERC20Params = z.infer<typeof MintPrivateERC20Schema>;

// COTI Private ERC20 ABI for minting (corrected signature)
const ERC20_ABI = [
    "function mint(address account, uint64 amount)",
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function decimals() view returns (uint8)",
    "function totalSupply() view returns (uint256)",
    "function balanceOf(address account) view returns (uint256)",
    "function owner() view returns (address)"
];

export const mintPrivateErc20TokenAction: Action = {
    name: "MINT_PRIVATE_ERC20",
    similes: [
        "MINT_ERC20",
        "MINT_TOKEN",
        "CREATE_TOKENS",
        "MINT_PRIVATE_TOKEN",
        "ISSUE_TOKENS",
        "GENERATE_TOKENS",
        "MINT_PRIVATE_ERC20_TOKEN"
    ],
    description: "Mint new private ERC20 tokens on the COTI blockchain. This creates new tokens and assigns them to the specified recipient address.",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            const text = message.content?.text || "";
            elizaLogger.info(`MINT_PRIVATE_ERC20 validation - text: "${text}"`);
            
            // Check for minting keywords and ERC20/token context
            const isMintQuery = /\b(mint|create|issue|generate).*\b(token|erc20)\b/i.test(text);
            elizaLogger.info(`MINT_PRIVATE_ERC20 validation - isMintQuery: ${isMintQuery}`);
            
            // Look for addresses (should have at least token address and recipient)
            const addressPattern = /0x[a-fA-F0-9]{40}/g;
            const addresses = text.match(addressPattern) || [];
            elizaLogger.info(`MINT_PRIVATE_ERC20 validation - addresses found: ${addresses.length}`);
            
            // Look for amount
            const amountPattern = /\b\d+(\.\d+)?\b/;
            const hasAmount = amountPattern.test(text);
            elizaLogger.info(`MINT_PRIVATE_ERC20 validation - amount found: ${hasAmount}`);
            
            // Should be a mint query with addresses and amount, but not NFT-related
            const isNftQuery = /\b(nft|721|metadata|uri)\b/i.test(text);
            const isTransferQuery = /\b(transfer|send|move)\b/i.test(text);
            
            const shouldHandle = isMintQuery && addresses.length >= 2 && hasAmount && !isNftQuery && !isTransferQuery;
            elizaLogger.info(`MINT_PRIVATE_ERC20 validation - returning ${shouldHandle}`);
            return shouldHandle;
        } catch (error) {
            elizaLogger.error(`MINT_PRIVATE_ERC20 validation error:`, error);
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting MINT_PRIVATE_ERC20 action");

            let params: MintPrivateERC20Params;

            // Try to get parameters from message content first
            if (message.content?.tokenAddress && message.content?.toAddress && message.content?.amount) {
                params = MintPrivateERC20Schema.parse(message.content);
            } else {
                // Extract from message text
                const text = message.content?.text || "";
                const addresses = text.match(/0x[a-fA-F0-9]{40}/g) || [];
                const amountMatch = text.match(/\b(\d+(?:\.\d+)?)\b/);

                if (addresses.length < 2) {
                    throw new Error("Please provide both token contract address and recipient address");
                }

                if (!amountMatch || !amountMatch[1]) {
                    throw new Error("Please specify the amount of tokens to mint");
                }

                params = {
                    tokenAddress: addresses[0]!,
                    toAddress: addresses[1]!,
                    amount: amountMatch[1]
                };
            }

            elizaLogger.info("Parsed mint parameters:", params);

            const walletProvider = initCotiWalletProvider(runtime);
            const wallet = walletProvider.getWallet();
            const provider = new ethers.providers.JsonRpcProvider(process.env.COTI_RPC_URL || "https://testnet.coti.io/rpc");

            // Create contract instance for reading
            const contractReader = new ethers.Contract(
                params.tokenAddress,
                ERC20_ABI,
                provider
            );



            elizaLogger.info(`Minting ${params.amount} tokens to ${params.toAddress}`);

            // Get token info for formatting and validate contract
            const [name, symbol, decimals] = await Promise.all([
                contractReader.name().catch(() => "Unknown Token"),
                contractReader.symbol().catch(() => "UNKNOWN"),
                contractReader.decimals().catch(() => 18)
            ]);

            // Check if the contract has a mint function and if caller has permission
            try {
                // Try to check if the contract has an owner function (common in mintable tokens)
                const owner = await contractReader.owner().catch(() => null);
                const callerAddress = walletProvider.getAddress();

                elizaLogger.info(`Contract owner: ${owner}, Caller: ${callerAddress}`);

                if (owner && owner.toLowerCase() !== callerAddress.toLowerCase()) {
                    throw new Error(`Only the contract owner (${owner}) can mint tokens. Current caller: ${callerAddress}`);
                }
            } catch (error) {
                elizaLogger.warn("Could not verify minting permissions:", error);
            }

            // Convert amount to wei (considering decimals)
            const amountWei = ethers.utils.parseUnits(params.amount, decimals);

            // COTI private ERC20 uses uint64 for amount, so we need to ensure it fits
            const maxUint64 = ethers.BigNumber.from("18446744073709551615"); // 2^64 - 1
            if (amountWei.gt(maxUint64)) {
                throw new Error(`Amount ${amountWei.toString()} exceeds maximum uint64 value. Please use a smaller amount.`);
            }

            elizaLogger.info(`Attempting to mint ${amountWei.toString()} wei (${params.amount} ${symbol}) to ${params.toAddress}`);

            // Encode the transaction data
            const contractInterface = new ethers.utils.Interface(ERC20_ABI);
            const data = contractInterface.encodeFunctionData("mint", [
                params.toAddress,
                amountWei
            ]);

            // Send the transaction using the wallet
            const tx = await wallet.sendTransaction({
                to: params.tokenAddress,
                data: data,
                gasLimit: params.gasLimit || 500000 // Increased gas limit
            });

            const receipt = await tx.wait();

            const responseText = `Successfully minted private ERC20 tokens! 🎉
Token: ${name} (${symbol})
Contract: ${params.tokenAddress}
Amount: ${params.amount} ${symbol}
Recipient: ${params.toAddress}
Transaction Hash: ${tx.hash}
Block Number: ${receipt?.blockNumber}`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        tokenAddress: params.tokenAddress,
                        amount: params.amount,
                        recipient: params.toAddress,
                        transactionHash: tx.hash,
                        blockNumber: receipt?.blockNumber?.toString(),
                        tokenName: name,
                        tokenSymbol: symbol,
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    tokenAddress: params.tokenAddress,
                    amount: params.amount,
                    amountWei: amountWei.toString(),
                    recipient: params.toAddress,
                    transactionHash: tx.hash,
                    blockNumber: receipt?.blockNumber?.toString(),
                    tokenName: name,
                    tokenSymbol: symbol,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in MINT_PRIVATE_ERC20 action:", error);
            const errorMessage = `Failed to mint private ERC20 tokens: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Mint 1000 tokens from 0x17339ed50Bc8d54E14B959B59d07eB90D0d6249F to 0x7E22eF3C47Bfc99344D070993ADB07d5D6BE2372",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll mint those private ERC20 tokens for you.",
                    action: "MINT_PRIVATE_ERC20",
                },
            },
        ],
    ],
};
